import { useEffect, useRef, useState } from 'react'

import { Scope } from '@unform/core'
import { Checkbox } from '@vtex/shoreline'

import BadgeIsValid from '~/components/BadgeIsValid'
import Panel from '~/components/Panel'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import {
  AUDIENCE_AGE_RANGE_OPTIONS,
  AUDIENCE_GENDER_OPTIONS,
  AUDIENCE_REGION_OPTIONS,
  AUDIENCE_SOCIAL_CLASS_OPTIONS
} from '~/modules/retailMedia/constants/audienceTargeting'

import { useCampaignForm } from '../../hooks/useCampaignForm'
import AudienceImport from '../AudienceImport'
import Interests from './Interests'
import MultiSelectField from './MultiSelectField'

import {
  BroadAudienceHint,
  ErrorText,
  FieldGroup,
  GatingArea,
  GatingOption,
  SectionTitle,
  SupportText
} from './styles'

// Exported so callers can type the state they pass back through
// onInvalidReasonChange without widening it to a plain string, which would
// stop it from type-checking against i18next's own key union.
export type AudienceTargetingInvalidReasonKey =
  | 'rm:campaign.create.AudienceTargeting.alerts.demographicsRequired'
  | 'rm:campaign.create.AudienceTargeting.audienceImport.errors.required'
  | 'rm:campaign.create.AudienceTargeting.interests.errors.required'

type AudienceTargetingProps = {
  // Reports whether the panel's required fields — geographic region, age
  // range and gender (FR-017m) — are filled, replacing CampaignTargeting's
  // keyword/category validity as the gate feeding the investment step and
  // the submit button for the new flow (FR-017k). Optional so existing/other
  // callers don't need to pass it.
  handleSectionValidity?(isValid: boolean): void
  // Every requirement currently unmet -- empty once the section is valid.
  // Demographics, a pending audience file and empty interests are
  // independent failures, so callers must show all of them at once instead
  // of picking just one (found in review: showing only the highest-priority
  // reason kept surfacing "select region/age/gender" after that was already
  // done, whenever the real blocker was the audience file or interests).
  onInvalidReasonChange?(keys: AudienceTargetingInvalidReasonKey[]): void
  // Toggle-button fields never emit a native input, so Banner's form
  // `onChange` (which bumps `formVersion` for ReviewStep) cannot see them.
  // Fired after the initial mount, once a demographic/interest selection
  // actually changes.
  onFieldsChange?(): void
  // Quando informado, define se o painel começa aberto (padrão: aberto)
  initialCollapsibleState?: boolean
}

/**
 * Demographic/geographic parameters (FR-017, FR-017g) plus the interests
 * and imported-audience segmentation gate (FR-017a to FR-017j). Offsite-only
 * — DV360 and Meta share every option here (FR-017d); the single
 * difference between channels is the imported-audience option's label.
 */
const AudienceTargeting: React.FC<AudienceTargetingProps> = ({
  handleSectionValidity,
  onInvalidReasonChange,
  onFieldsChange,
  initialCollapsibleState
}) => {
  const { t } = useAPPTranslation()
  // Both start unmarked (FR-017h). Marking one reveals its fill-in area;
  // unmarking recollapses it without discarding what was filled (FR-017j) —
  // the area's own fields keep their unform-registered values regardless of
  // whether they're currently mounted, they just stop rendering. Lifted
  // into the shared context (rather than local state) so ReviewStep can
  // read them for the Segmentation row's label.
  const {
    selectedChannel,
    interestsEnabled,
    setInterestsEnabled,
    audienceImportEnabled,
    setAudienceImportEnabled,
    audienceFile,
    setAudienceFile,
    restrictOffsiteFlow
  } = useCampaignForm()

  // Geographic region, age range and gender are required (Publya
  // OrderCreateRequest). Social class stays optional (FR-017b).
  const [region, setRegion] = useState<string[]>([])
  const [ageRange, setAgeRange] = useState<string[]>([])
  const [gender, setGender] = useState<string[]>([])
  const [interests, setInterests] = useState<string[]>([])
  const sectionIsValid =
    region.length > 0 && ageRange.length > 0 && gender.length > 0

  // Marking the audience-import gate without ever finishing the upload left
  // the campaign submittable with the checkbox checked and no file behind it
  // -- the intent silently dropped, campaign goes out with broad reach
  // instead (PR #745 review). Blocks submit until either a file is attached
  // or the gate is unmarked again.
  const audienceFileMissing = audienceImportEnabled && !audienceFile
  // Same failure mode as audienceFileMissing, for the other gated option
  // (PR #745 review): marking "Interesses" without picking any sub-group
  // silently submitted with no interest segmentation at all.
  const interestsMissing = interestsEnabled && interests.length === 0

  const sectionIsReady =
    sectionIsValid && !audienceFileMissing && !interestsMissing

  // Computed inside the effect, not as a render-time array: an array built
  // every render is a new reference every time, so keying the effect on it
  // directly would re-fire (and re-notify the parent) on every render
  // instead of only when one of the three booleans actually changes.
  useEffect(() => {
    const invalidReasonKeys: AudienceTargetingInvalidReasonKey[] = [
      !sectionIsValid &&
        ('rm:campaign.create.AudienceTargeting.alerts.demographicsRequired' as const),
      audienceFileMissing &&
        ('rm:campaign.create.AudienceTargeting.audienceImport.errors.required' as const),
      interestsMissing &&
        ('rm:campaign.create.AudienceTargeting.interests.errors.required' as const)
    ].filter((key): key is AudienceTargetingInvalidReasonKey => !!key)

    handleSectionValidity?.(sectionIsReady)
    onInvalidReasonChange?.(invalidReasonKeys)
  }, [
    sectionIsValid,
    audienceFileMissing,
    interestsMissing,
    sectionIsReady,
    handleSectionValidity,
    onInvalidReasonChange
  ])

  const onFieldsChangeRef = useRef(onFieldsChange)
  onFieldsChangeRef.current = onFieldsChange

  const notifyFieldsChange = () => onFieldsChangeRef.current?.()

  const audienceImportLabel =
    selectedChannel === 'meta'
      ? t('rm:campaign.create.AudienceTargeting.audienceImport.meta.title')
      : t('rm:campaign.create.AudienceTargeting.audienceImport.dv360.title')

  // Ata de 15/09, item 3.4: a área diz o que a opção faz — combina a segmentação
  // escolhida com dados de conversão da VTEX — em vez de anunciar disponibilidade
  // futura, que era o texto de quando o envio ainda não existia.
  const audienceImportDescription = t(
    selectedChannel === 'meta'
      ? 'rm:campaign.create.AudienceTargeting.audienceImport.meta.description'
      : 'rm:campaign.create.AudienceTargeting.audienceImport.dv360.description'
  )

  return (
    <Panel
      title={t('rm:campaign.create.AudienceTargeting.title')}
      description={t('rm:campaign.create.AudienceTargeting.description')}
      isCollapsible
      initialCollapsibleState={initialCollapsibleState}
      iconLeft={<BadgeIsValid isValid={sectionIsReady} />}
    >
      <Scope path="audienceTargeting">
        <FieldGroup>
          <MultiSelectField
            name="region"
            required
            label={t('rm:campaign.create.AudienceTargeting.region.title')}
            allLabel={t('rm:campaign.create.AudienceTargeting.all')}
            options={AUDIENCE_REGION_OPTIONS.map(value => ({
              value,
              label: t(`rm:campaign.create.AudienceTargeting.region.${value}`)
            }))}
            onChange={value => {
              setRegion(value)
              notifyFieldsChange()
            }}
          />

          <MultiSelectField
            name="ageRange"
            required
            label={t('rm:campaign.create.AudienceTargeting.ageRange.title')}
            allLabel={t('rm:campaign.create.AudienceTargeting.all')}
            options={AUDIENCE_AGE_RANGE_OPTIONS.map(value => ({
              value,
              label: value
            }))}
            onChange={value => {
              setAgeRange(value)
              notifyFieldsChange()
            }}
          />

          <MultiSelectField
            name="gender"
            required
            label={t('rm:campaign.create.AudienceTargeting.gender.title')}
            allLabel={t('rm:campaign.create.AudienceTargeting.allGender')}
            options={AUDIENCE_GENDER_OPTIONS.map(value => ({
              value,
              label: t(`rm:campaign.create.AudienceTargeting.gender.${value}`)
            }))}
            onChange={value => {
              setGender(value)
              notifyFieldsChange()
            }}
          />

          <MultiSelectField
            name="socialClass"
            label={t('rm:campaign.create.AudienceTargeting.socialClass.title')}
            allLabel={t('rm:campaign.create.AudienceTargeting.all')}
            options={AUDIENCE_SOCIAL_CLASS_OPTIONS.map(value => ({
              value,
              label: t(
                `rm:campaign.create.AudienceTargeting.socialClass.${value}`
              )
            }))}
            onChange={notifyFieldsChange}
          />
        </FieldGroup>

        <SectionTitle>
          {t('rm:campaign.create.AudienceTargeting.segmentation.title')}
        </SectionTitle>

        <GatingOption>
          <Checkbox
            checked={interestsEnabled}
            onChange={() => setInterestsEnabled(!interestsEnabled)}
          >
            {t('rm:campaign.create.AudienceTargeting.interests.title')}
          </Checkbox>

          {/* Kept mounted (hidden via the native attribute, not
              conditionally rendered) so unmarking the gate doesn't
              unregister Interests' Unform field and discard the selection
              — FR-017j says unmarking only hides the area, it must
              preserve what was filled (review comment). */}
          <GatingArea hidden={!interestsEnabled}>
            <Interests
              onChange={value => {
                setInterests(value)
                notifyFieldsChange()
              }}
            />

            {interestsMissing && (
              <ErrorText role="alert">
                {t(
                  'rm:campaign.create.AudienceTargeting.interests.errors.required'
                )}
              </ErrorText>
            )}
          </GatingArea>
        </GatingOption>

        {!restrictOffsiteFlow && (
          <GatingOption>
            <Checkbox
              checked={audienceImportEnabled}
              onChange={() => setAudienceImportEnabled(!audienceImportEnabled)}
            >
              {audienceImportLabel}
            </Checkbox>

            {audienceImportEnabled && (
              <GatingArea>
                <SupportText>{audienceImportDescription}</SupportText>

                <AudienceImport
                  value={audienceFile ?? null}
                  onChange={value => {
                    setAudienceFile(value)
                    notifyFieldsChange()
                  }}
                />

                {audienceFileMissing && (
                  <ErrorText role="alert">
                    {t(
                      'rm:campaign.create.AudienceTargeting.audienceImport.errors.required',
                      { label: audienceImportLabel }
                    )}
                  </ErrorText>
                )}
              </GatingArea>
            )}
          </GatingOption>
        )}
      </Scope>

      {!interestsEnabled && !audienceImportEnabled && (
        <BroadAudienceHint>
          {t('rm:campaign.create.AudienceTargeting.broadAudienceHint')}
        </BroadAudienceHint>
      )}
    </Panel>
  )
}

export default AudienceTargeting
